/**
 * Backofen in der Vorderansicht: Bedienleiste mit Heizart-Symbol, großer
 * Temperaturanzeige und Drehknopf, darunter die Tür mit Sichtfenster.
 *
 * Die Bedienleiste ist wie bei echten Öfen in beiden Modi dunkel; die
 * Temperatur steht darauf in heller Schrift, beim Heizen im Akzentton. Die Heizart wird zusätzlich als
 * Symbol gezeigt, wie auf echten Backöfen.
 */
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, group, label, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/** Heizarten. */
export const HEATING_MODES = {
  topBottom: 'Ober-/Unterhitze',
  convection: 'Umluft',
  grill: 'Grill',
  bottom: 'Unterhitze',
} as const;

/** Kennung einer Heizart. */
export type HeatingMode = keyof typeof HEATING_MODES;

/** Maße des Ofens. */
export const OVEN = {
  x: 38,
  y: 26,
  width: 164,
  height: 110,
  panel: 30,
} as const;

/** Sichtfenster in der Tür. */
export const OVEN_WINDOW = {
  x: OVEN.x + 16,
  y: OVEN.y + OVEN.panel + 18,
  width: OVEN.width - 32,
  height: OVEN.height - OVEN.panel - 30,
} as const;

/** Symbol der Heizart in einem Quadrat (Mitte cx, cy). */
export function heatingSymbol(cx: number, cy: number, mode: HeatingMode, active: boolean): SceneNode {
  const size = 18;
  const x = cx - size / 2;
  const y = cy - size / 2;
  const tone = active ? 'accent' : 'snow';
  const parts: SceneNode[] = [rect(x, y, size, size, 2, { stroke: 'snow', strokeWidth: STROKE.fine })];
  if (mode === 'topBottom' || mode === 'grill') {
    parts.push(line(x + 3, y + 3.5, x + size - 3, y + 3.5, { stroke: tone, strokeWidth: STROKE.detail }));
  }
  if (mode === 'topBottom' || mode === 'bottom') {
    parts.push(
      line(x + 3, y + size - 3.5, x + size - 3, y + size - 3.5, { stroke: tone, strokeWidth: STROKE.detail }),
    );
  }
  if (mode === 'grill') {
    parts.push(
      shape(
        path()
          .move(x + 4, y + 8)
          .line(x + 6.5, y + 11)
          .line(x + 9, y + 8)
          .line(x + 11.5, y + 11)
          .line(x + 14, y + 8)
          .toString(),
        { stroke: tone, strokeWidth: STROKE.fine },
      ),
    );
  }
  if (mode === 'convection') {
    parts.push(circle(cx, cy, 5, { stroke: tone, strokeWidth: STROKE.detail }));
    parts.push(line(cx - 3, cy, cx + 3, cy, { stroke: tone, strokeWidth: STROKE.fine }));
    parts.push(line(cx, cy - 3, cx, cy + 3, { stroke: tone, strokeWidth: STROKE.fine }));
  }
  return group(parts);
}

/** Lage der Temperaturanzeige. */
export const DISPLAY = { x: OVEN.x + 44, y: OVEN.y + 6, width: 74, height: 22 } as const;

/**
 * Gehäuse, Bedienleiste und Tür. Das Innere des Fensters (Heizstäbe, Licht,
 * Backgut) zeichnet die Vorlage selbst zwischen inside und door.
 */
export function ovenParts(options: {
  readonly temperature: number;
  readonly mode: HeatingMode;
  readonly heating: boolean;
  readonly knobAngle: number;
}): { body: SceneNode[]; door: SceneNode[] } {
  const { x, y, width, height, panel } = OVEN;
  const knob = { cx: x + width - 20, cy: y + panel / 2 + 1 };
  const body: SceneNode[] = [
    // Gehäuse
    shape(roundedRectPath(x, y, width, height, 10), styles.solid('paper')),
    // Bedienleiste, in beiden Modi dunkel
    shape(roundedRectPath(x + 6, y + 5, width - 12, panel - 6, 6), styles.solid('cavity')),
    heatingSymbol(x + 25, y + panel / 2 + 1, options.mode, options.heating),
    // Temperaturanzeige: dunkles Feld, helle große Ziffern
    rect(DISPLAY.x, DISPLAY.y + 1, DISPLAY.width, DISPLAY.height - 2, 4, {
      fill: 'cavity',
      stroke: 'deep',
      strokeWidth: STROKE.fine,
    }),
    label(DISPLAY.x + DISPLAY.width / 2, DISPLAY.y + 17, `${Math.round(options.temperature)} °C`, 16, {
      fill: options.heating ? 'accent' : 'snow',
      weight: 800,
    }),
    // Drehknopf mit Markierung
    circle(knob.cx, knob.cy, 8.5, styles.solid('paper')),
    group([line(knob.cx, knob.cy - 2, knob.cx, knob.cy - 7, { stroke: 'ink', strokeWidth: STROKE.detail })], {
      transform: { rotate: options.knobAngle, ox: knob.cx, oy: knob.cy },
    }),
    // Kontrolllampe
    circle(knob.cx - 18, knob.cy, 2.6, { fill: options.heating ? 'accent' : 'deep', stroke: 'none' }),
  ];
  const door: SceneNode[] = [
    // Griff
    shape(roundedRectPath(x + 24, y + panel + 5, width - 48, 6, 3), styles.solid('deep')),
    // Rahmen des Fensters
    shape(
      roundedRectPath(OVEN_WINDOW.x, OVEN_WINDOW.y, OVEN_WINDOW.width, OVEN_WINDOW.height, 7),
      styles.outline(),
    ),
    // Spiegelung auf der Scheibe
    shape(
      path()
        .move(OVEN_WINDOW.x + 12, OVEN_WINDOW.y + OVEN_WINDOW.height - 10)
        .line(OVEN_WINDOW.x + 34, OVEN_WINDOW.y + 10)
        .toString(),
      { stroke: 'paper', strokeWidth: STROKE.detail, opacity: 0.85 },
    ),
    // Füße
    rect(x + 12, y + height, 14, 4, 1.5, styles.flat('ink')),
    rect(x + width - 26, y + height, 14, 4, 1.5, styles.flat('ink')),
  ];
  return { body, door };
}
